import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { AdminSidebar } from '../../components/AdminSidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Search, CheckCircle, AlertTriangle, AlertCircle, XCircle, Trash2, Wrench } from 'lucide-react';

import { API_URL } from '../../utils/api';
import { getErrorMessage } from '../../utils/errors';

export default function AdminErrorLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('unresolved'); // 'all', 'unresolved', 'resolved'

  useEffect(() => {
    fetchLogs();
  }, [filter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter === 'unresolved') params.resolved = false;
      if (filter === 'resolved') params.resolved = true;
      const response = await axios.get(`${API_URL}/admin/error-logs`, { params });
      setLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch error logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const resolveLog = async (logId) => {
    try {
      await axios.post(`${API_URL}/admin/error-logs/${logId}/resolve`);
      toast.success("Error marked as resolved");
      fetchLogs();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to resolve error"));
    }
  };

  const deleteLog = async (logId) => {
    if (!window.confirm("Delete this error log entry?")) return;
    try {
      await axios.delete(`${API_URL}/admin/error-logs/${logId}`);
      toast.success("Error log deleted");
      fetchLogs();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to delete error log"));
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-orange-500" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      critical: 'bg-red-100 text-red-700',
      error: 'bg-orange-100 text-orange-700',
      warning: 'bg-yellow-100 text-yellow-700',
    };
    return colors[severity] || 'bg-gray-100 text-gray-700';
  };

  const getTypeBadge = (type) => {
    const colors = {
      payment_failed: 'bg-red-50 text-red-600',
      order_failed: 'bg-orange-50 text-orange-600',
      ivr_error: 'bg-blue-50 text-blue-600',
      auth_error: 'bg-purple-50 text-purple-600',
      system_error: 'bg-gray-50 text-gray-600',
    };
    const labels = {
      payment_failed: 'Payment Failed',
      order_failed: 'Order Failed',
      ivr_error: 'IVR Error',
      auth_error: 'Auth Error',
      system_error: 'System Error',
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${colors[type] || 'bg-gray-50 text-gray-600'}`}>
        {labels[type] || type}
      </span>
    );
  };

  const filteredLogs = logs.filter(log => {
    const q = search.toLowerCase();
    return (
      (log.message || '').toLowerCase().includes(q) ||
      (log.details || '').toLowerCase().includes(q) ||
      (log.error_type || '').toLowerCase().includes(q) ||
      (log.suggested_action || '').toLowerCase().includes(q)
    );
  });

  const unresolvedCount = logs.filter(l => !l.is_resolved).length;

  return (
    <div className="min-h-screen bg-paper">
      <AdminSidebar />
      <div className="ml-64 p-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-serif text-3xl font-bold text-ink mb-2">Error Logs</h1>
          <p className="text-ink/60 mb-8">Monitor and resolve system errors</p>

          <div className="flex gap-4 mb-6 items-center flex-wrap">
            <div className="relative max-w-md flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                    placeholder="Search errors..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                />
            </div>
            <div className="flex gap-2">
              <Button
                variant={filter === 'unresolved' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('unresolved')}
              >
                Unresolved
              </Button>
              <Button
                variant={filter === 'resolved' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('resolved')}
              >
                Resolved
              </Button>
              <Button
                variant={filter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('all')}
              >
                All
              </Button>
            </div>
          </div>

          <div className="text-sm text-ink/50 mb-4">
            {filteredLogs.length} error{filteredLogs.length !== 1 ? 's' : ''} shown
            {unresolvedCount > 0 && filter !== 'resolved' && (
              <span className="ml-2 text-red-500 font-medium">({unresolvedCount} unresolved)</span>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div></div>
          ) : filteredLogs.length === 0 ? (
            <div className="bg-white rounded-lg border border-border p-12 text-center">
              <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-4" />
              <p className="text-ink/60 text-lg">
                {filter === 'unresolved' ? 'No unresolved errors' : 'No errors found'}
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">Sev.</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead>Suggested Action</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLogs.map((log) => (
                    <TableRow key={log.id} className={log.severity === 'critical' ? 'bg-red-50/30' : ''}>
                      <TableCell>
                        <div className="flex items-center" title={log.severity}>
                          {getSeverityIcon(log.severity)}
                        </div>
                      </TableCell>
                      <TableCell>{getTypeBadge(log.error_type)}</TableCell>
                      <TableCell>
                        <div className="max-w-xs">
                          <p className="text-sm font-medium truncate" title={log.message}>{log.message}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-xs">
                          <p className="text-xs text-ink/60 truncate" title={log.details}>{log.details || '-'}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-xs">
                          {log.suggested_action ? (
                            <p className="text-xs text-blue-600 truncate" title={log.suggested_action}>
                              <Wrench className="inline h-3 w-3 mr-1" />
                              {log.suggested_action}
                            </p>
                          ) : (
                            <span className="text-xs text-ink/30">-</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600">
                          {log.source || 'system'}
                        </span>
                      </TableCell>
                      <TableCell className="text-ink/60 text-xs whitespace-nowrap">
                        {new Date(log.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {!log.is_resolved && (
                            <Button size="sm" variant="outline" onClick={() => resolveLog(log.id)} title="Mark as resolved">
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            </Button>
                          )}
                          {log.is_resolved && (
                            <span className="inline-flex items-center text-xs text-green-600 px-2">
                              <CheckCircle className="h-3 w-3 mr-1" /> Resolved
                            </span>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => deleteLog(log.id)} title="Delete log">
                            <Trash2 className="h-4 w-4 text-red-400" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
